import React from 'react'
import { DateTime as DateTimeLuxon } from 'luxon'
import { isEmpty } from 'lodash'
import { DateTime } from '@labrav/react-components'
import { standardLabraDateFormat } from '@labrav/common'

import { ShowExpirationDateWithIcon } from '../common/components/ShowExpirationDateWithIcon/ShowExpirationDateWithIcon'
import { isOfferExpired } from '../common/utils/isOfferExpired'
import {
  OfferStatus,
  OfferTypeRenew,
  PrivateOffer,
} from '../modules/privateOffer/reducer'
import { CloudMarketplace } from '../modules/productsListing/types'

export const excludedStatuses = ['OFFER_VIEWED', 'OFFER_EMAIL_SENT']

export const getOfferLinkPrivateOffer = (
  cloudType: CloudMarketplace,
  privateOffer: PrivateOffer
) => {
  switch (cloudType) {
    case 'AWS': {
      return privateOffer?.labraOfferUrl
    }
    case 'AZURE': {
      return privateOffer?.acceptanceLink
    }
    case 'GCP': {
      return privateOffer?.cloudOfferUrl
    }
    case 'REDHAT': {
      return ''
    }
    default:
      return ''
  }
}

export const getProductIdFromPrivateOffer = (
  offer: PrivateOffer,
  cloud: CloudMarketplace
) => {
  switch (cloud) {
    case 'AWS': {
      return offer.awsProductId!
    }
    case 'AZURE': {
      return (
        offer.labraProductId || offer.cloudProductId || offer.awsProductId || ''
      )
    }
    case 'GCP': {
      return offer.labraProductId || ''
    }
    case 'REDHAT': {
      return ''
    }
  }
}

export const getExpirationDateFromPrivateOfferV1 = (
  offer: PrivateOffer,
  cloud: CloudMarketplace
) => {
  switch (cloud) {
    case 'AWS': {
      return offer.offerExpirationDate && !isEmpty(offer.offerExpirationDate)
        ? ((<DateTime date={offer.offerExpirationDate || ''} />) as never)
        : '-'
    }
    case 'AZURE': {
      return offer.acceptBy && !isEmpty(offer.acceptBy)
        ? ((<DateTime date={offer.acceptBy || ''} />) as never)
        : '-'
    }
    case 'GCP': {
      return offer.offerExpirationDate && !isEmpty(offer.offerExpirationDate)
        ? ((<DateTime date={offer.offerExpirationDate || ''} />) as never)
        : '-'
    }
    case 'REDHAT': {
      return ''
    }
  }
}

export const getExpirationDateFromPrivateOffer = (
  offer: PrivateOffer,
  cloud: CloudMarketplace,
  shouldShowExpiringSoon?: boolean
) => {
  switch (cloud) {
    case 'AWS': {
      return offer.offerExpirationDate &&
        !isEmpty(offer.offerExpirationDate) ? (
        excludedStatuses.includes(offer.offerStatus) ? (
          <ShowExpirationDateWithIcon
            date={offer.offerExpirationDate || ''}
            daysToCompare={5}
          />
        ) : (
          DateTimeLuxon.fromISO(offer.offerExpirationDate).toFormat(
            standardLabraDateFormat
          )
        )
      ) : (
        '-'
      )
    }
    case 'AZURE': {
      return offer.acceptBy && !isEmpty(offer.acceptBy)
        ? ((
            <ShowExpirationDateWithIcon
              date={offer.acceptBy || ''}
              daysToCompare={5}
              shouldShowExpiringSoon={shouldShowExpiringSoon}
            />
          ) as never)
        : '-'
    }
    case 'GCP': {
      return offer.offerExpirationDate && !isEmpty(offer.offerExpirationDate)
        ? ((
            <ShowExpirationDateWithIcon
              date={offer.offerExpirationDate || ''}
              daysToCompare={5}
            />
          ) as never)
        : '-'
    }
    case 'REDHAT': {
      return ''
    }
  }
}

export const getKeyCloudAccount = (cloud: CloudMarketplace) => {
  switch (cloud) {
    case 'AWS': {
      return 'awsOfferId'
    }
    case 'AZURE': {
      return 'azurePrivateOfferId'
    }
    case 'GCP': {
      return 'cloudOfferId'
    }
    case 'REDHAT': {
      return ''
    }
  }
}

export const getPrivateOfferRedirectUrl = ({
  cloudType,
  offer,
  externalBaseURL,
  productId,
  cloud,
  accountId,
}: {
  cloudType: CloudMarketplace
  offer: PrivateOffer
  externalBaseURL?: string
  productId: string
  cloud?: string
  accountId?: string
}) => {
  const isOfferDraft =
    cloudType === 'AZURE'
      ? offer.offerStatus === OfferStatus.OFFER_IN_DRAFT || !!offer.draft
      : offer.draft

  const isAWSRenewalDraftOffer =
    isOfferDraft &&
    offer?.type === OfferTypeRenew.RENEWAL &&
    !isEmpty(offer?.labraSubscriptionId) &&
    cloudType === 'AWS'

  if (!productId) {
    console.warn('Cannot generate redirect URL: productId is missing')
    return '/flyout/products-listing'
  }

  if (!offer.privateOfferId) {
    console.warn('Cannot generate redirect URL: privateOfferId is missing')
    if (externalBaseURL) {
      const hasProductId = !externalBaseURL.endsWith('/product-listing')
      return hasProductId ? externalBaseURL : `${externalBaseURL}/${productId}`
    }
    if (cloud && accountId) {
      return `/flyout/${cloud}/${accountId}/products-listing/${productId}`
    }
    return `/flyout/products-listing/${productId}`
  }

  const getBasePathForSubscriptions = (baseURL?: string) => {
    if (!baseURL) {
      if (cloud && accountId) {
        return `/flyout/${cloud}/${accountId}`
      }
      return '/flyout'
    }
    return baseURL
      .replace(/\/product-listing$/, '')
      .replace(/\/products-listing$/, '')
  }

  const baseURL = externalBaseURL
    ? externalBaseURL
    : cloud && accountId
      ? `/flyout/${cloud}/${accountId}/products-listing`
      : '/flyout/products-listing'

  const isExternalURL = !!externalBaseURL
  const externalURLHasProductId =
    isExternalURL && !baseURL.endsWith('/product-listing')

  const awsRenewalDraftRedirectURL = offer?.labraSubscriptionId
    ? `${getBasePathForSubscriptions(externalBaseURL)}/subscriptions/${offer.labraSubscriptionId}/edit/${offer.privateOfferId}`
    : isExternalURL
      ? externalURLHasProductId
        ? `${baseURL}/private-offers/${offer.privateOfferId}/edit`
        : `${baseURL}/${productId}/private-offers/${offer.privateOfferId}/edit`
      : `${baseURL}/${productId}/private-offers/${offer.privateOfferId}/edit`

  return isAWSRenewalDraftOffer
    ? awsRenewalDraftRedirectURL
    : isExternalURL
      ? externalURLHasProductId
        ? `${baseURL}${
            isOfferDraft
              ? `/private-offers/${offer.privateOfferId}/edit`
              : `/private-offers/${offer.privateOfferId}/details`
          }`
        : `${baseURL}/${productId}${
            isOfferDraft
              ? `/private-offers/${offer.privateOfferId}/edit`
              : `/private-offers/${offer.privateOfferId}/details`
          }`
      : `${baseURL}/${productId}${
          isOfferDraft
            ? `/private-offers/${offer.privateOfferId}/edit`
            : `/private-offers/${offer.privateOfferId}/details`
        }`
}

export const getStatusOffer = (
  {
    offerStatus: status,
    offerExpirationDate,
    draft: isDraft,
    acceptBy,
  }: PrivateOffer,
  cloudType: CloudMarketplace,
  options?: { skipAzureExpiryStatus?: boolean }
) => {
  const offerExpiration = offerExpirationDate
    ? new Date(offerExpirationDate)
    : null
  const azureOfferExpiration = acceptBy ? new Date(acceptBy) : null
  const expirationDate =
    cloudType === 'AZURE' ? azureOfferExpiration : offerExpiration

  if (
    status === OfferStatus.PRIVATE_OFFER_FAILED ||
    status === OfferStatus.PRIVATE_OFFER_RESTRCITED ||
    status === OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS
  ) {
    return status
  }
  if (status === OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS && isDraft) {
    return OfferStatus.PRIVATE_OFFER_IN_DRAFT
  }
  if (
    expirationDate &&
    isOfferExpired(expirationDate, status) &&
    !(options?.skipAzureExpiryStatus && cloudType === 'AZURE')
  ) {
    return OfferStatus.PRIVATE_OFFER_EXPIRED
  }
  return status
}

export const getOfferRenewalType = (type?: OfferTypeRenew) => {
  switch (type) {
    case OfferTypeRenew.NEW: {
      return 'New'
    }
    case OfferTypeRenew.RENEWAL: {
      return 'ABO'
    }
    default: {
      return '-'
    }
  }
}
